import * as React from 'react';
import { Collection, dtoToCollectionProps } from './collection';
import { getPrivateAppsData, getTagsDataBySlugs } from '../../api/api';
import { FetchStatus } from '../view-rapper';
import { biLogger } from '../../bi';
import { config } from '../../config';
import type { MarketplaceStore } from '../../component-decorators/with-marketplace-store';
import { withMarketplaceStore } from '../../component-decorators/with-marketplace-store';
import type { appMarketPageViewParams } from '@wix/bi-logger-market/v2/types';
import type { IFetcherInjected } from '../../component-decorators/with-fetcher';
import { WithFetcher } from '../../component-decorators/with-fetcher';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';
import { composer } from '../../component-decorators/composer';
import { CenteredLoader } from '../centered-loader';
import { ErrorPageType, Origin } from '../..';
import { CollectionSEO } from './collection-seo';
import { useConfigContext } from '../../config-context';
import { ErrorPageContainer } from '../error-page/error-page-container';
import { getPathForBi, getWidgetReferral } from '../../common/bi-events';
import { withExperiments } from '../../component-decorators/with-experiments';
import type { GetWebSolutionsByTagsResponse } from '@wix/ambassador-marketplace/types';
import { appMarketPageView } from '@wix/bi-logger-market/v2';
import type { AppMarketRpcClient } from '../../types/app-market-rpc-client';

interface ICollectionContainerBaseProps
  extends Partial<InjectedExperimentsProps> {
  slug: string;
}

interface ICollectionContainerProps
  extends IFetcherInjected,
    ICollectionContainerBaseProps {
  marketplaceStore?: MarketplaceStore;
  locale: string;
  countryCode: string;
}

export interface ICollectionFetcherProps
  extends ICollectionContainerBaseProps {}

interface ICollectionContainerState {
  isFetchingData: FetchStatus;
}

const dataFetcher = async (
  slug: string,
  experiments,
  locale = 'en',
  countryCode: string,
  privateAppsTagSlug: string,
  privateAppsTagId: string,
  rpcClient?: AppMarketRpcClient,
) => {
  const showPrivateApps = slug === privateAppsTagSlug;

  const slugToFetch = slug === 'newest-apps' ? 'newest-apps-v2' : slug;

  if (showPrivateApps) {
    return getPrivateAppsData();
  }
  return getTagsDataBySlugs(
    [slugToFetch],
    locale,
    countryCode,
    true,
    true,
    rpcClient,
  );
};

@withMarketplaceStore
@withExperiments
class CollectionContainer extends React.Component<
  ICollectionContainerProps,
  ICollectionContainerState
> {
  constructor(props: ICollectionContainerProps) {
    super(props);
    this.state = { isFetchingData: FetchStatus.FETCHING };
  }

  async componentDidMount() {
    this.sendPageViewBi(this.props.fetchData);
  }

  async componentDidUpdate(prevProps) {
    const slugToFetch =
      this?.props?.slug === 'newest-apps' ? 'newest-apps-v2' : this.props.slug;

    if (this.props.slug !== prevProps.slug) {
      this.props.reFetch(() =>
        dataFetcher(
          slugToFetch,
          this.props.experiments,
          this.props.locale,
          this.props.countryCode,
          this.props.marketplaceStore.privateAppsTagSlug,
          this.props.marketplaceStore.privateAppsTagId,
        ),
      );

      this.sendPageViewBi(this.props.fetchData);
    }
  }

  sendPageViewBi = (dto: GetWebSolutionsByTagsResponse): void => {
    const pageViewParams: appMarketPageViewParams = {
      app_id: null,
      market: config.originForBI,
      navigation_source: this.props?.marketplaceStore?.route?.searchLocation,
      tag_name: this.props.marketplaceStore?.route?.slug?.replace(/-/g, '_'),
      tag_type: 'collection',
      msid: this.props.marketplaceStore.metaSiteId,
      referral_info: getPathForBi(
        this.props?.marketplaceStore?.prevRoute?.path,
      ),
      search_term: this.props.marketplaceStore?.prevRoute?.query,
      widget_referral: getWidgetReferral(),
      utm_id: this?.props?.marketplaceStore?.route?.utm_id,
      referral_tag_name: this.props?.marketplaceStore?.prevRoute?.slug?.replace(
        /-/g,
        '_',
      ),
      referral_tag_type: getPathForBi(
        this.props.marketplaceStore?.prevRoute?.path,
      ),
      referral_sub_tag_name:
        this.props.marketplaceStore?.prevRoute?.subCategories?.join(','),
      is_external:
        this.props?.marketplaceStore?.route?.referral &&
        this.props?.marketplaceStore?.route?.referral !== 'manage_apps',
    };
    biLogger.report(appMarketPageView(pageViewParams));
  };

  render() {
    return <Collection {...dtoToCollectionProps(this.props.fetchData)} />;
  }
}

function isValidCollectionPageData(fetchData) {
  return (
    fetchData &&
    fetchData.tagsAndSubCategories &&
    fetchData.tagsAndSubCategories[0]
  );
}

export const CollectionFetcher = composer()
  .withMarketplaceStore()
  .withExperiments()
  .compose(({ slug, experiments, marketplaceStore }) => {
    const { locale, countryCode, rpcClient } = useConfigContext();

    const slugToFetch = slug === 'newest-apps' ? 'newest-apps-v2' : slug;

    return (
      <WithFetcher
        loader={<CenteredLoader />}
        initialFetch={() =>
          dataFetcher(
            slugToFetch,
            experiments,
            locale,
            countryCode,
            marketplaceStore.privateAppsTagSlug,
            marketplaceStore.privateAppsTagId,
            rpcClient,
          )
        }
        fetchMemoizationId={`collection-${slug}-${locale}`}
      >
        {({ fetchData, reFetch }) => {
          return (
            <CollectionContainerWrapper
              slug={slug}
              locale={locale}
              countryCode={countryCode}
              fetchData={fetchData}
              reFetch={reFetch}
              useNewApi={true}
              sortTagsByGeo={true}
              experiments={experiments}
              marketplaceStore={marketplaceStore}
            />
          );
        }}
      </WithFetcher>
    );
  });

const CollectionContainerWrapper = ({
  slug,
  locale,
  countryCode,
  reFetch,
  fetchData,
  useNewApi,
  sortTagsByGeo,
  experiments,
  marketplaceStore,
}: any) => {
  React.useEffect(() => {
    const slugToFetch = slug === 'newest-apps' ? 'newest-apps-v2' : slug;

    reFetch(() =>
      dataFetcher(
        slugToFetch,
        experiments,
        locale,
        countryCode,
        marketplaceStore?.privateAppsTagSlug,
        marketplaceStore?.privateAppsTagId,
      ),
    );
  }, [slug, locale]);
  return isValidCollectionPageData(fetchData) ? (
    <>
      {config.origin === Origin.STANDALONE && (
        <CollectionSEO
          collectionName={fetchData.tagsAndSubCategories[0].tag.name}
          collectionDesc={fetchData.tagsAndSubCategories[0].tag.description}
          collectionSlug={slug}
        />
      )}
      <CollectionContainer
        slug={slug}
        fetchData={fetchData}
        reFetch={reFetch}
        locale={locale}
        countryCode={countryCode}
      />
    </>
  ) : (
    <ErrorPageContainer slug={slug} errorPageType={ErrorPageType.NOT_FOUND} />
  );
};
